import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import TweetCard from './components/dashboard/TweetCard';
import BotReplyCard from './components/dashboard/BotReplyCard';
import IntroAnimation from './components/IntroAnimation';
import './App.css';
import DashboardStats from './components/dashboard/DashboardStats';
import BotsIPTable from './components/dashboard/BotsIPTable';
import CyberRoom from './components/navbar/CyberRoom/CyberRoom';
import AddPoliticianPopup from './components/sidebar/AddPoliticianPopup';
import DeepfakeScanner from './components/sidebar/DeepfakeScanner';
import BotNetworks from './components/sidebar/BotNetworks';
import ListAnalysis from './components/sidebar/ListAnalysis';
import MamdaniPage from './pages/MamdaniPage';
import { MamdaniProvider } from './context/MamdaniContext';

// ─────────────────────────────────────────
// קומפוננטת טאבים לפי סנטימנט
// ─────────────────────────────────────────
function SentimentTabs({ replies, username, tweetId }) {
  const [activeTab, setActiveTab] = useState('negative');
  const [mode, setMode] = useState('tweet');
  const [allReplies, setAllReplies] = useState([]);

  useEffect(() => {
    if (mode === 'all' && username) {
      fetch(`https://api.anti-bot.ai/api/bot-replies-all/${username}`)
        .then(r => r.json())
        .then(d => { if (d.success) setAllReplies(d.replies); })
        .catch(console.error);
    }
  }, [mode, username]);

  const source = mode === 'tweet' ? replies : allReplies;

  const tabs = [
    { key: 'negative', label: 'מתנגדים', color: '#C47B82' },
    { key: 'positive', label: 'תומכים',  color: '#6BA882' },
    { key: 'neutral',  label: 'ניטרלים', color: '#B7A05A' },
  ];

  const filtered = source.filter(r => (r.sentiment || 'neutral') === activeTab);

  return (
    <div>
      {/* טוגל ציוץ זה / כל הזמן */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', justifyContent: 'flex-end' }}>
        {[['tweet', 'ציוץ זה'], ['all', 'כל הזמן']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setMode(val)}
            style={{
              padding: '4px 12px',
              borderRadius: '20px',
              border: '1px solid',
              fontSize: '12px',
              cursor: 'pointer',
              borderColor: mode === val ? '#D89B5A' : 'rgba(74,63,55,0.40)',
              background: mode === val ? 'rgba(216,155,90,0.15)' : 'transparent',
              color: mode === val ? '#D89B5A' : 'rgba(183,169,154,0.60)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* טאבים */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {tabs.map(tab => {
          const count = source.filter(r => (r.sentiment || 'neutral') === tab.key).length;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '10px',
                border: '1px solid rgba(74,63,55,0.50)',
                cursor: 'pointer',
                background: 'transparent',
                color: activeTab === tab.key ? tab.color : 'rgba(183,169,154,0.45)',
                fontWeight: activeTab === tab.key ? 700 : 400,
                fontSize: '13px',
              }}
            >
              {tab.emoji} {tab.label}
              <span style={{
                marginRight: '6px',
                background: activeTab === tab.key ? tab.color : 'rgba(255,255,255,0.1)',
                color: activeTab === tab.key ? '#000' : 'rgba(255,255,255,0.5)',
                borderRadius: '10px',
                padding: '1px 7px',
                fontSize: '11px',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* תגובות */}
      <div className="bot-replies-container">
        {filtered.length === 0 ? (
          <div className="no-bots-message">אין תגובות בקטגוריה זו</div>
        ) : (
          filtered.map(reply => (
            <BotReplyCard key={reply.id} reply={reply} />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// App
// ─────────────────────────────────────────
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tweet, setTweet] = useState(null);
  const [botReplies, setBotReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState(null);
  const [activeTab, setActiveTab] = useState('netanyahu');
  const [loadingTweet, setLoadingTweet] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [currentPage, setCurrentPage] = useState('monitor');
  const [showAddPopup, setShowAddPopup] = useState(false);

  const [tabs, setTabs] = useState([
    { id: 'netanyahu',      name: 'נתניהו' },
    { id: 'AvigdorLiberman', name: 'אביגדור ליברמן' },
    { id: 'naftalibennett', name: 'נפתלי בנט' },
    { id: 'yairlapid',      name: 'יאיר לפיד' },
    { id: 'gadi_eisenkot',  name: 'גדי איזנקוט' },
    { id: 'itamarbengvir',  name: 'איתמר בן גביר' },
    { id: 'bezalelsm',      name: 'בצלאל סמוטריץ׳' },
    { id: 'gantzbe',        name: 'בני גנץ' },
  ]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token === 'ANTIBOT_LOGGED_IN') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    const newSocket = io('https://api.anti-bot.ai', {
      transports: ['polling', 'websocket']
    });

    setSocket(newSocket);
    fetchInitialData();

    newSocket.on('connect', () => setConnected(true));
    newSocket.on('disconnect', () => setConnected(false));
    newSocket.on('bot_replies_update', (data) => setBotReplies(data.replies));
    newSocket.on('tweet_update', (data) => setTweet(data));

    return () => newSocket.close();
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchTweetByAccount(activeTab);
    }
  }, [activeTab, isLoggedIn]);

  const fetchInitialData = () => {
    fetchTweetByAccount('netanyahu');
    setLoading(false);
  };

  const fetchTweetByAccount = (username) => {
    setLoadingTweet(true);
    setLoadingReplies(true);

    fetch(`https://api.anti-bot.ai/api/tweets-by-account/${username}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.tweets.length > 0) {
          const latestTweet = data.tweets[0];
          setTweet(latestTweet);
          fetchBotRepliesForTweet(latestTweet.twitter_id);
        } else {
          setTweet(null);
          setBotReplies([]);
          setLoadingReplies(false);
        }
        setLoadingTweet(false);
      })
      .catch(() => {
        setTweet(null);
        setBotReplies([]);
        setLoadingTweet(false);
        setLoadingReplies(false);
      });
  };

  const fetchBotRepliesForTweet = (tweetId) => {
    fetch(`https://api.anti-bot.ai/api/bot-replies-for-tweet/${tweetId}`)
      .then(res => res.json())
      .then(data => {
        setBotReplies(data.success ? data.replies : []);
        setLoadingReplies(false);
      })
      .catch(() => {
        setBotReplies([]);
        setLoadingReplies(false);
      });
  };

  const handlePoliticianAdded = ({ username, name }) => {
    const hebrewOnly = name?.split(' - ').find(part => /[\u0590-\u05FF]/.test(part)) || name || username;
    setTabs(prev => [...prev, { id: username, name: hebrewOnly }]);
    setActiveTab(username);
  };

  const handleLogin = () => {
    localStorage.setItem('authToken', 'ANTIBOT_LOGGED_IN');
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setTweet(null);
    setBotReplies([]);
    setLoading(true);
    if (socket) socket.close();
  };

  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const mainContent = (
    <div style={{ padding: '32px', margin: '0 20px 20px 20px' }}>
      <div className="app-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px' }}>⏳</div>
            <p>טוען נתונים...</p>
          </div>
        ) : (
          <>
            <div className="dashboard-container">
              <DashboardStats />
            </div>

            {/* טאבים פוליטיקאים */}
            <div className="tweet-tabs-wrapper">
              <div className="tweet-tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.name}
                  </button>
                ))}
                <button
                  className="tab-button tab-add"
                  onClick={() => setShowAddPopup(true)}
                >
                  +
                </button>
              </div>
            </div>

            {/* גריד 50/50: ציוץ | תגובות בוטים */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'stretch', marginBottom: '24px' }}>
              {/* ציוץ אחרון */}
              <div style={{ height: '430px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                {loadingTweet ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner"></div>
                    <p>טוען ציוץ...</p>
                  </div>
                ) : tweet ? (
                  <TweetCard tweet={tweet} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>📭 אין ציוצים להצגה</p>
                  </div>
                )}
              </div>

              {/* תגובות בוטים — גובה קבוע עם גלילה פנימית */}
              <div style={{ background: 'rgba(38,33,29,0.85)', backdropFilter: 'blur(12px)', border: '1px solid #4A3F37', borderRadius: '16px', padding: '24px', height: '430px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <h2 className="section-title" style={{ color: '#D89B5A', flexShrink: 0 }}>
                  <img src="/image/Cards/Bots.png" alt="bots" style={{ width: 20, height: 20, marginLeft: 8, filter: 'brightness(0) saturate(100%) invert(67%) sepia(24%) saturate(820%) hue-rotate(350deg) brightness(92%) contrast(88%)' }} />
                  תגובות בוטים שזוהו ({botReplies.length})
                </h2>
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {loadingReplies ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="spinner"></div>
                      <p>טוען תגובות...</p>
                    </div>
                  ) : (
                    <SentimentTabs
                      replies={botReplies}
                      username={activeTab}
                      tweetId={tweet?.twitter_id}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* שורה שנייה: טבלת IP | רשתות בוטים */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={{ height: '380px', overflowY: 'auto', borderRadius: '16px' }}>
                <BotsIPTable />
              </div>
              <div style={{ height: '380px', overflowY: 'auto', borderRadius: '16px', background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(12px)', border: '1px solid var(--border-color)' }}>
                <BotNetworks />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <MamdaniProvider>
    <div style={{ minHeight: "100vh", position: "relative" }}>
      <Layout onLogout={handleLogout} connected={connected} onPageChange={setCurrentPage}>

        {currentPage === 'cyber' && <CyberRoom />}
        {currentPage === 'deepfake' && <DeepfakeScanner />}
        {currentPage === 'listanalysis' && <ListAnalysis />}
        {currentPage === 'mamdani' && <MamdaniPage />}

        {currentPage !== 'cyber' && currentPage !== 'deepfake' && currentPage !== 'listanalysis' && currentPage !== 'mamdani' && (
          <>
            {showIntro && (
              <IntroAnimation
                onComplete={() => setShowIntro(false)}
                overlapOffset={1100}
                navbarProps={{
                  onLogout: handleLogout,
                  connected: connected,
                  onPageChange: setCurrentPage
                }}
              >
                {mainContent}
              </IntroAnimation>
            )}
            {!showIntro && mainContent}
            {showAddPopup && (
              <AddPoliticianPopup
                onClose={() => setShowAddPopup(false)}
                onAdded={handlePoliticianAdded}
              />
            )}
          </>
        )}

      </Layout>
    </div>
    </MamdaniProvider>
  );
}

export default App;